import type { UserProfile } from 'generated/openapi';
import { type ComponentProps, memo, useMemo } from 'react';

import { useGetDuoDetails } from '@/features/duos/hooks/useGetDuoDetails';
import { getMutualServers } from '@/features/games/valorant/utils/servers';
import { useGetProfile } from '@/features/profile/hooks/useGetProfile';
import { RarityCard } from '@/shared/components/ui/rarityCard';
import { useScrollPosition } from '@/shared/hooks/useScrollPosition/useScrollPosition';
import { cn } from '@/shared/utils/cn';

import { DuoActivityActions } from './actions';
import { DuoActivityAgentsPlayed } from './agentsPlayed';
import { DuoActivityCardsPair } from './cardsPair';
import { DuoActivityHeader } from './header';
// TODO: Uncomment when player data service implements impact rating
// import { DuoActivityImpactRating } from './impactRating';
import { DuoActivityStats } from './stats';

export interface DuoActivityProps extends ComponentProps<'div'> {
  userId: string;
}

export const DuoActivity = memo(function DuoActivity(props: DuoActivityProps) {
  const { className, userId, ...rest } = props;
  const [ref, { isAtBottom }] = useScrollPosition();

  const { data } = useGetDuoDetails({ userId });
  const duo = data.duo;

  // First partner is always the current user
  const [currentUserPartnerData, otherPartnerData] = duo.partners;

  // Get profiles for both partners
  const { data: currentUserProfile } = useGetProfile({
    userId: currentUserPartnerData.userId,
    options: {
      select: data => data.profile as UserProfile,
    },
  });

  const { data: partnerProfile } = useGetProfile({
    userId: otherPartnerData.userId,
    options: {
      select: data => data.profile as UserProfile,
    },
  });

  // Get rarity and compatibility from profiles (use partner's rarity as duo rarity)
  const duoRarity = partnerProfile.rarity ?? 'COMMON';
  const playstyleCompatibility =
    partnerProfile.compatibilityScores?.playstyle ?? 0;
  const vibeCompatibility = partnerProfile.compatibilityScores?.vibe ?? 0;

  const currentUserAgents = Object.entries(currentUserPartnerData.agents)
    .sort((a, b) => b[1].matchCount - a[1].matchCount)
    .map(([id, stats]) => ({
      id,
      count: stats.matchCount,
    }));

  const partnerAgents = Object.entries(otherPartnerData.agents)
    .sort((a, b) => b[1].matchCount - a[1].matchCount)
    .map(([id, stats]) => ({
      id,
      count: stats.matchCount,
    }));

  const mutualServers = useMemo(
    () =>
      getMutualServers(
        currentUserProfile.valorantServers,
        partnerProfile.valorantServers,
      ),
    [currentUserProfile.valorantServers, partnerProfile.valorantServers],
  );

  return (
    <RarityCard.Outer
      {...rest}
      rarity={duoRarity}
      className={cn('relative', className)}
    >
      <DuoActivityCardsPair
        leftRarity={duoRarity}
        rightRarity={duoRarity}
        leftRiotPuuid={currentUserProfile.riotPuuid}
        rightRiotPuuid={partnerProfile.riotPuuid}
        leftImage={currentUserProfile.pictureUrl}
        rightImage={partnerProfile.pictureUrl}
        leftName={currentUserProfile.displayName}
        rightName={partnerProfile.displayName}
        leftIsPremium={currentUserProfile.isPremium}
        rightIsPremium={partnerProfile.isPremium}
        className="absolute -top-20 left-1/2 z-20 -translate-x-1/2"
      />
      <RarityCard.Inner
        ref={ref}
        className="no-scrollbar flex h-full min-h-0 w-full flex-1 flex-col gap-0 overflow-x-hidden overflow-y-auto scroll-smooth"
      >
        <DuoActivityHeader
          isMyUserPremium={currentUserProfile.isPremium}
          partnerDisplayName={partnerProfile.displayName}
          isPartnerPremium={partnerProfile.isPremium}
          lastPlayedAt={duo.stats.lastPlayedAt}
          playstyleCompatibility={playstyleCompatibility}
          vibeCompatibility={vibeCompatibility}
          level={duo.progress.level}
          matchCount={duo.progress.matchCount}
          nextLevelRequirements={duo.progress.nextLevelRequirements}
        />
        <div className="bg-cold-950 flex flex-col gap-1.5 px-2 pt-4 pb-2">
          {/* TODO: Uncomment when player data service implements impact rating */}
          {/* <DuoActivityImpactRating impactRating={duo.impactRating} /> */}
          <DuoActivityStats stats={duo.stats} mutualServers={mutualServers} />
          <DuoActivityAgentsPlayed
            currentUserAgents={currentUserAgents}
            partnerAgents={partnerAgents}
            currentUserDisplayName={currentUserProfile.displayName}
            partnerDisplayName={partnerProfile.displayName}
            currentUserPictureUrl={currentUserProfile.pictureUrl}
            partnerPictureUrl={partnerProfile.pictureUrl}
            currentUserFavoriteAgentId={currentUserProfile.mainValorantAgentId}
            partnerFavoriteAgentId={partnerProfile.mainValorantAgentId}
          />
        </div>
        <DuoActivityActions
          partnerUserId={otherPartnerData.userId}
          canReview={duo.canReview}
          className={cn(
            'bg-cold-950 sticky bottom-0 z-20',
            !isAtBottom && 'shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.5)]',
          )}
        />
      </RarityCard.Inner>
    </RarityCard.Outer>
  );
});
